import { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import { useStringsContext } from '../../../contexts/strings';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSocial } from './hooks/social';
import { Engagement } from './types';
import svStrings from '../../../sv-strings';
import { SxStyle } from '../../../types';

const contentTypeString: Record<string, string> = {
  news: svStrings.social.contentNews,
};

const styles: SxStyle = {
  dataGridContainer: {
    height: 'calc(100% - 80px)',
    width: '100%',
  },
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

// Map hook data to grid data.
const engagementToRow = (e: Engagement): ReactionRow => {
  return {
    id: e.contentId,
    type: e.contentType,
    latest: moment(e.lastEngagementAt).format('YYYY-MM-DD HH:mm'),
    content: e.contentTitle,
    numberOfReactions: e.numberOfReactions,
    numberOfComments: 0, // TODO: Add comments to the grid.
  };
};

type ReactionRow = {
  id: string;
  type: string;
  latest: string;
  content: string | null;
  numberOfReactions: number;
  numberOfComments: number;
};

interface Props {
  selectedAbbrv: string;
}

function SocialReactionsList({ selectedAbbrv }: Props) {
  const { strings } = useStringsContext();
  const [loading, setLoading] = useState(true);
  const [totalNumberOfReactions, setTotalNumberOfReactions] = useState(0);
  const [dataGridRows, setDataGridRows] = useState<ReactionRow[]>([]);
  const { getEngagements } = useSocial();

  const getReactionResult = useCallback(async () => {
    try {
      const engagements = await getEngagements(selectedAbbrv);

      if (engagements) {
        const data = engagements.map(engagementToRow);
        const total = engagements.reduce((acc, engagment) => acc + engagment.numberOfReactions, 0);

        setDataGridRows(data);
        setTotalNumberOfReactions(total);
      } else {
        setDataGridRows([]);
        setTotalNumberOfReactions(0);
      }
    } catch (error) {
      // do nothing
    }

    setLoading(false);
  }, [selectedAbbrv, getEngagements]);

  useEffect(() => {
    getReactionResult();
  }, [getReactionResult]);

  const initialColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'latest',
        headerName: strings.social.reactionsFieldLatest,
        flex: 1,
      },
      {
        field: 'type',
        headerName: strings.social.reactionsFieldContentType,
        flex: 1,
        maxWidth: 120,
        valueGetter: (value) => contentTypeString[value],
      },
      {
        field: 'content',
        headerName: strings.social.reactionsFieldContent,
        flex: 1,
      },
      {
        field: 'numberOfReactions',
        headerName: strings.social.reactionsFieldNumberOfReactions,
        flex: 1,
      },
      // TODO Disabled for now
      // {
      //  field: 'numberOfComments',
      //  headerName: strings.socialInteractions.reactionsFieldNumberOfComments,
      //  flex: 1,
      // },
    ],
    [strings]
  );

  return (
    <>
      <Stack direction="column" spacing={0} marginBottom={2} justifyContent="center">
        <Typography variant="h6">{strings.social.reactionsTitle}</Typography>
        <Typography>{`${totalNumberOfReactions} ${strings.social.reactionsTotal}`}</Typography>
      </Stack>
      <Paper elevation={2} sx={styles.dataGridContainer}>
        <DataGrid
          sx={styles.dataGrid}
          rows={dataGridRows}
          columns={initialColumns}
          getRowId={(row) => row.id}
          loading={loading}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'latest',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
      </Paper>
    </>
  );
}

export default SocialReactionsList;
